import { collectionOf } from "@turf/invariant";
import axios from "axios";
import { AUCKLAND_BBOX, DEFAULT_FETCH_TIMEOUT_MS } from "../../Constants/Constants.js";
import {
  SEARCH_API_BASE_URL,
  SEARCH_API_MAX_LIMIT,
  SEARCH_PARAM_COUNTRY,
  SEARCH_PARAM_FORWARD_GEOCODE_TYPES,
  SEARCH_PARAM_LANGUAGE,
  SEARCH_PARAM_REVERSE_GEOCODE_TYPE,
} from "../../Constants/MapboxSearch.js";
import { DataValidationError } from "../../Errors/DataValidationError.js";
import { MapboxApiError } from "../../Errors/MapboxApiError.js";
import { ATMapboxTransformationService } from "./ATMapboxTransformationService.js";

export class ATMapboxGeocodingService {
  /** @type {number} */
  _defaultLimit;
  /** @type {string} */
  _accessToken;
  /** @type {AxiosInstance} */
  _axiosInstance;
  /** @type {Logger} */
  _logger;

  _mapboxTransformationService;

  /**
   * Construct the ATMapboxGeocodingService. This service facilitates the communication with the Mapbox Geocoding API.
   * @param {MapboxApiOptions} mapbox Mapbox API configuration options
   * @param {string} cdnBaseUrl CDN Base URL
   * @param {Logger} logger Logger
   * @param {number} defaultLimit Default maximum number of results Mapbox should return
   */
  constructor(mapbox, cdnBaseUrl, logger, defaultLimit = SEARCH_API_MAX_LIMIT) {
    const { accessToken, timeout } = mapbox;
    this._accessToken = accessToken;
    this._defaultLimit = defaultLimit;
    this._axiosInstance = axios.create({
      baseURL: SEARCH_API_BASE_URL,
      timeout: timeout ?? DEFAULT_FETCH_TIMEOUT_MS,
    });
    this._logger = logger;
    this._mapboxTransformationService = new ATMapboxTransformationService(mapbox, cdnBaseUrl, logger);
  }

  /**
   * Send a forwardGeocode query to Mapbox.
   * @param {ForwardGeocodeOptions} options Forward geocoding options
   * @returns {Promise<PromiseSettledResult<GeocodeResultFeatures>>} Geocoding result
   */
  async forwardGeocode({ query, proximity, limit }) {
    try {
      const resultLimit = limit?.mapbox ?? this._defaultLimit;
      if (resultLimit <= 0) {
        return { status: "fulfilled", value: [] };
      }

      const params = {
        access_token: this._accessToken,
        bbox: AUCKLAND_BBOX,
        country: SEARCH_PARAM_COUNTRY,
        language: SEARCH_PARAM_LANGUAGE,
        limit: SEARCH_API_MAX_LIMIT, // always request Mapbox's max then filter down to limit later
        types: SEARCH_PARAM_FORWARD_GEOCODE_TYPES.join(","),
      };
      if (proximity) {
        params.proximity = `${proximity.join()}`;
      }

      let mapboxResponse;
      try {
        mapboxResponse = await this._axiosInstance.get(`/forward/${encodeURIComponent(query)}`, { params });
      } catch (error) {
        throw new MapboxApiError(error);
      }

      this._verifyMapboxResponse(mapboxResponse);

      const value = await this._mapboxTransformationService.transformFeatures(
        mapboxResponse.data.features,
        resultLimit
      );
      return { status: "fulfilled", value };
    } catch (error) {
      this._logger?.error?.(`Mapbox Forward geocoder: ${error.name} - ${error.message}`);
      return {
        status: "rejected",
        reason: error,
      };
    }
  }

  /**
   * Send a reverseGeocode query to Mapbox.
   * @param {[number, number]} coordinates Coordinates to reverse geocode as [longitude, latitude]
   * @returns {Promise<PromiseSettledResult<GeocodeResultFeatures>>} Geocoding result
   */
  async reverseGeocode(coordinates) {
    try {
      const params = {
        access_token: this._accessToken,
        language: SEARCH_PARAM_LANGUAGE,
        limit: 1, // only get the closest match to the given coordinates
        types: SEARCH_PARAM_REVERSE_GEOCODE_TYPE,
      };

      let mapboxResponse;
      try {
        mapboxResponse = await this._axiosInstance.get(`/reverse/${coordinates.join()}`, { params });
      } catch (error) {
        throw new MapboxApiError(error);
      }

      this._verifyMapboxResponse(mapboxResponse);

      const value = await this._mapboxTransformationService.transformFeatures(mapboxResponse.data.features);
      return { status: "fulfilled", value };
    } catch (error) {
      this._logger?.error?.(`Mapbox Reverse geocoder: ${error.name} - ${error.message}`);
      return {
        status: "rejected",
        reason: error,
      };
    }
  }

  // Verify the response received from Mapbox (see https://docs.mapbox.com/api/search/search/#response-forward-geocoding
  // and https://docs.mapbox.com/api/search/search/#response-reverse-geocoding)
  _verifyMapboxResponse(response) {
    const featureCollection = response.data;

    try {
      // turf.collectionOf() asserts this is a FeatureCollection of Points
      collectionOf(featureCollection, "Point", "verifyMapboxResponse");
    } catch (error) {
      throw new DataValidationError(`Invalid feature collection: ${error.message}`);
    }

    featureCollection.features.forEach((feature) => {
      if (!feature.geometry?.coordinates) throw new DataValidationError("Feature without coordinates");
      if (!feature.properties?.id) throw new DataValidationError("Feature without id");
      if (!feature.properties?.feature_name) throw new DataValidationError("Feature without feature_name");
      if (!feature.properties?.place_name) throw new DataValidationError("Feature without place_name");
      if (!feature.properties?.place_type?.length) throw new DataValidationError("Feature without place_type");
      if (!feature.properties?.place_type.find((placeType) => SEARCH_PARAM_FORWARD_GEOCODE_TYPES.includes(placeType)))
        throw new DataValidationError("Feature with an invalid place_type");
    });
  }
}
